import {
    Autocomplete,
    ListItem,
    ListItemButton,
    TextField,
} from '@mui/material'
import { GetRostersByNameQuery, RosterType } from '__generated__/graphql'
import { FC, useCallback, useEffect, useState } from 'react'
import { usePagination } from 'shared/hooks/usePagination'
import { GetRostersByName } from './RostersAutocomplete.gql'
import { RostersAutocompleteProps } from './RostersAutocompleteProps'

const LIMIT = 10
/**
 * multiple + single autocompletes are separated to workaround MUI typing
 * @param RostersAutocompleteProps
 * @returns MUI Autocomplete with paginated options (single-select)
 */
const RostersAutocomplete: FC<RostersAutocompleteProps<false>> = ({
    error,
    helperText,
    allMyRosters,
    onlyMyRosters,
    includeDrafts,
    includeClosed,
    size,
    placeholder,
    label,
    rosterTypes = Object.values(RosterType),
    initialValue,
    onSingleSelectionChange: onSelectionChange = () => {},
    textFieldSx,
    ...props
}) => {
    const [name, setName] = useState('')
    const [open, setOpen] = useState(false)
    const [debouncedInput, setDebouncedInput] = useState<string>('')
    const [selectedRoster, setSelectedRoster] = useState<
        GetRostersByNameQuery['rostersByName']['data'][number] | null
    >(!!initialValue && !Array.isArray(initialValue) ? initialValue : null)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedInput(name)
        }, 1000)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    useCallback(() => {
        refetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedInput])

    const divName = 'rostersAutcomplete'
    const { data, loading, refetch } = usePagination<
        GetRostersByNameQuery['rostersByName']['data'][number]
    >({
        query: GetRostersByName,
        variables: {
            name: name,
            page: 1,
            limit: LIMIT,
            onlyMyRosters,
            includeAllMyRosters: allMyRosters,
            includeDrafts,
            includeClosedRosters: includeClosed,
        },
        skip: !open,
        queryPath: 'rostersByName',
        limit: LIMIT,
        trackedDivClassName: divName,
    })

    return (
        <Autocomplete
            fullWidth
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            disablePortal
            loading={loading}
            getOptionLabel={(option) => option.rosterName}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, state, ownerState) => {
                return (
                    <ListItem
                        {...props}
                        sx={{
                            padding: 0,
                        }}
                        className={divName}
                        key={option.id}
                    >
                        <ListItemButton>{option.rosterName}</ListItemButton>
                    </ListItem>
                )
            }}
            onChange={(event, newValue) => {
                setSelectedRoster(newValue)
                onSelectionChange?.(newValue)
            }}
            value={selectedRoster}
            onInputChange={(e, value, reason) => {
                if (reason === 'reset') setName('')
                else setName(value)
            }}
            {...props}
            options={data || []}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                    sx={textFieldSx}
                />
            )}
        />
    )
}

export default RostersAutocomplete
